import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Stack,
  Badge,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugIcon,
  Star as StarIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useReleaseNotes, ReleaseNote } from '../../hooks/useReleaseNotes';

interface ReleaseNotesModalProps {
  open: boolean;
  onClose: () => void;
}

// Styled Modal Container
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  outline: 'none',
  maxWidth: '800px',
  width: '90%',
  maxHeight: '80vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  [theme.breakpoints.down('sm')]: {
    width: '95%',
    maxHeight: '85vh',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: '1px solid #e0e0e0',
  position: 'relative',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  color: theme.palette.grey[600],
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#ccc',
    borderRadius: '3px',
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  border: '1px solid #e0e0e0',
  borderRadius: '8px !important',
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(0, 0, 2, 0),
  },
}));

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '8px 8px 0 0',
  minHeight: '56px',
  '&.Mui-expanded': {
    minHeight: '56px',
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(2, 0),
    '&.Mui-expanded': {
      margin: theme.spacing(2, 0),
    },
  },
}));

const VersionChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontWeight: 600,
  fontSize: '0.875rem',
}));

const CategoryContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const CategoryHeader = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
}));

const ChangeItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
}));

const ChangeTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
}));

const ChangeDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontFamily: theme.typography.fontFamily,
}));

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'features':
      return <StarIcon color="primary" />;
    case 'improvements':
      return <BuildIcon color="action" />;
    case 'bugFixes':
      return <BugIcon color="error" />;
    default:
      return <BuildIcon />;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'features':
      return 'New Features';
    case 'improvements':
      return 'Improvements';
    case 'bugFixes':
      return 'Bug Fixes';
    default:
      return category;
  }
};

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ open, onClose }) => {
  const { releaseNotes, markVersionAsViewed } = useReleaseNotes();
  const [expandedVersions, setExpandedVersions] = useState<string[]>([]);
  const theme = useTheme();

  // Expand the latest version by default when modal opens
  useEffect(() => {
    if (open && releaseNotes.length > 0) {
      const latestVersion = releaseNotes[0].version;
      setExpandedVersions([latestVersion]);
    }
  }, [open, releaseNotes]);

  const handleAccordionChange = (version: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedVersions(prev => [...prev, version]);
      // Note: Version tracking removed - badge is now hidden when modal opens
    } else {
      setExpandedVersions(prev => prev.filter(v => v !== version));
    }
  };

  const formatReleaseDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderChangeCategory = (category: string, changes?: Array<{ title: string; description: string }>) => {
    if (!changes || changes.length === 0) return null;

    return (
      <CategoryContainer key={category}>
        <CategoryHeader variant="subtitle1">
          {getCategoryIcon(category)}
          {getCategoryLabel(category)}
        </CategoryHeader>
        {changes.map((change, index) => (
          <ChangeItem key={index}>
            <ChangeTitle 
              variant="body1"
              sx={{ 
                fontFamily: theme.typography.fontFamily 
              }}
            >
              {change.title}
            </ChangeTitle>
            <ChangeDescription 
              variant="body2"
              sx={{ 
                fontFamily: theme.typography.fontFamily 
              }}
            >
              {change.description}
            </ChangeDescription>
          </ChangeItem>
        ))}
      </CategoryContainer>
    );
  };

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      aria-labelledby="release-notes-modal-title"
      aria-describedby="release-notes-modal-description"
    >
      <ModalContainer>
        <HeaderContainer>
          <CloseButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </CloseButton>
          <Typography 
            variant="h5" 
            id="release-notes-modal-title" 
            sx={{ 
              fontWeight: 600,
              fontFamily: theme.typography.h5.fontFamily 
            }}
          >
            Release Notes
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 1,
              fontFamily: theme.typography.fontFamily 
            }}
          >
            Stay updated with the latest features and improvements
          </Typography>
        </HeaderContainer>

        <ContentContainer id="release-notes-modal-description">
          {releaseNotes.map((release: ReleaseNote) => (
            <StyledAccordion
              key={release.version}
              expanded={expandedVersions.includes(release.version)}
              onChange={handleAccordionChange(release.version)}
            >
              <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <VersionChip label={release.version} />
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        marginBottom: 0.5,
                        fontFamily: theme.typography.h6.fontFamily 
                      }}
                    >
                      {release.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontFamily: theme.typography.fontFamily 
                      }}
                    >
                      Released on {formatReleaseDate(release.releaseDate)}
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummaryStyled>
              
              <AccordionDetails sx={{ padding: 3, paddingTop: 2 }}>
                {renderChangeCategory('features', release.changes.features)}
                {(release.changes.features?.length ?? 0) > 0 && ((release.changes.improvements?.length ?? 0) > 0 || (release.changes.bugFixes?.length ?? 0) > 0) && (
                  <Divider sx={{ my: 2 }} />
                )}
                {renderChangeCategory('improvements', release.changes.improvements)}
                {((release.changes.features?.length ?? 0) > 0 || (release.changes.improvements?.length ?? 0) > 0) && (release.changes.bugFixes?.length ?? 0) > 0 && (
                  <Divider sx={{ my: 2 }} />
                )}
                {renderChangeCategory('bugFixes', release.changes.bugFixes)}
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </ContentContainer>
      </ModalContainer>
    </StyledModal>
  );
};

export default ReleaseNotesModal;
